/**
 * Vertragsbaukasten Schritt 5: Assistent „Vertrag erstellen“ (Bauplan docs/vertragsbaukasten/bericht-vertraege.md 3.7, VB-16,
 * VB-17). Reine Logik: was noch fehlt, welche Klauseln wählbar sind, welche bedingten Teile greifen, welche Vorlage passt.
 * Gespeichert wird über die bisherigen Wege (createFromTemplate, saveDraft, syncSigners, contract_send).
 */
import { contextValue, type ContractContext, type ContractRole, type CustomVariableDef, type Finding, type Language } from "@talentkontor/domain";
import { t, nameIn } from "../../../../lib/sprache";
import type { TemplateRow } from "../daten";
import { bedingungSatz, istKlartextBedingung } from "./modell";

const leer = (v: unknown) => v === undefined || v === null || (typeof v === "string" && v.trim() === "");
/** wahr wie im Renderer: nicht leer, nicht 0, nicht false */
export const wahr = (v: unknown) => !(leer(v) || v === false || v === 0 || v === "0" || v === "false");
/** Wählbare Klausel: ui = "choice" aus dem Baukasten oder eine Ja/Nein-Angabe der Agentur beim Erstellen (VB-16, Bestand) */
const istWahl = (d: CustomVariableDef) => (d as { ui?: string }).ui === "choice" || (d.type === "boolean" && !d.fillable_by);

/** Schritt 2: Pflichtfelder „beim Erstellen“ ohne Wert (Standard und Vorbelegung sind schon eingesetzt). */
export function offeneFelder(defs: readonly CustomVariableDef[], values: Record<string, unknown>): CustomVariableDef[] {
  return defs.filter((d) => !istWahl(d) && !d.fillable_by && d.required && leer(values[d.key]));
}

/** Schritt 2: optionale Felder „beim Erstellen“ (können ausgefüllt werden, müssen nicht). */
export function optionaleFelder(defs: readonly CustomVariableDef[]): CustomVariableDef[] {
  return defs.filter((d) => !istWahl(d) && !d.fillable_by && !d.required);
}

/** Schritt 2: Felder, die ein Unterzeichner selbst einträgt (nur als Hinweis). */
export function unterzeichnerFelder(defs: readonly CustomVariableDef[]): { def: CustomVariableDef; rolle: ContractRole }[] {
  return defs.filter((d) => !istWahl(d) && !!d.fillable_by).map((d) => ({ def: d, rolle: d.fillable_by! }));
}

/** an: gewählt (Ja/Nein) oder noch offen (null: Pflicht-Ja/Nein ohne Standard und ohne Wert, Befund 6) */
export interface WahlKlausel { key: string; frage: string; hilfe: string | null; an: boolean | null; standard: boolean | null; pflicht: boolean }

/** Schritt 3: wählbare Klauseln mit aktuellem Stand (ohne Wert gilt der Standard der Vorlage). */
export function wahlKlauseln(defs: readonly CustomVariableDef[], values: Record<string, unknown>): WahlKlausel[] {
  return defs.filter(istWahl).map((d) => {
    const help = (d as { help?: { de: string; en: string } }).help;
    const standard = d.default === true ? true : d.default === false ? false : (d as { ui?: string }).ui === "choice" || !d.required ? false : null;
    const wert = values[d.key];
    return { key: d.key, frage: nameIn(d.label.de, d.label.en) || d.key, hilfe: help ? nameIn(help.de, help.en) || null : null, an: leer(wert) ? standard : wert === true, standard, pflicht: d.required };
  });
}

export interface BedingterTeil { pfad: string; satz: string; greift: boolean; /** fester Klartext-Satz (sonst „‚Feld‘ zutrifft“) */ klar: boolean }

/**
 * Schritt 3: bedingte Teile („Nur wenn …“) mit Klartext und ob sie bei diesem Vertrag greifen. Wahlklauseln stehen oben
 * gesondert; Bedingungen auf eigene Angaben werden mit dem eingetragenen Wert ausgewertet.
 */
export function bedingteTeile(html: string, ctx: ContractContext, defs: readonly CustomVariableDef[], values: Record<string, unknown>): BedingterTeil[] {
  const pfade = new Set<string>();
  for (const m of html.matchAll(/\{\{#if\s+([a-z][a-z0-9_]*\.[a-z][a-z0-9_]*)\s*\}\}/g)) pfade.add(m[1]!);
  const wahlKeys = new Set(defs.filter(istWahl).map((d) => d.key));
  const out: BedingterTeil[] = [];
  for (const pfad of pfade) {
    if (pfad.startsWith("var.")) {
      const key = pfad.slice(4);
      if (wahlKeys.has(key)) continue;
      out.push({ pfad, satz: bedingungSatz({ src: "custom", path: key }, defs), greift: wahr(values[key]), klar: false });
    } else {
      out.push({ pfad, satz: bedingungSatz({ src: "system", path: pfad }, defs), greift: wahr(contextValue(ctx, pfad)), klar: istKlartextBedingung({ src: "system", path: pfad }) });
    }
  }
  return out;
}

export type StammdatenLuecke =
  | { art: "marke"; pfade: string[] }
  | { art: "creator"; pfade: string[] }
  | { art: "sonst"; pfad: string };

const ANSCHRIFT = ["address_block", "street", "postal_code", "city", "country_name", "legal_name"];

/**
 * Schritt 2: fehlende Stammdaten aus der Prüfung (UNRESOLVED). Anschrift und Firmenname der Marke bzw. Anschrift des Creators
 * lassen sich an Ort und Stelle ergänzen; alles andere führt in die Stammdaten.
 */
export function fehlendeStammdaten(findings: readonly Finding[]): StammdatenLuecke[] {
  // eigene Angaben (var.*) fragt Schritt 2 selbst ab; sie sind keine Stammdaten (Befund 5)
  const pfade = [...new Set(findings.filter((f) => f.code === "UNRESOLVED").map((f) => String(f.params?.["path"] ?? "").split("|")[0]!.trim()).filter((p) => p && !p.startsWith("var.")))];
  const marke = pfade.filter((p) => p.startsWith("brand.") && ANSCHRIFT.includes(p.slice(6)));
  const creator = pfade.filter((p) => p.startsWith("talent.") && ANSCHRIFT.includes(p.slice(7)) && p !== "talent.legal_name");
  const rest = pfade.filter((p) => !marke.includes(p) && !creator.includes(p));
  return [
    ...(marke.length ? [{ art: "marke" as const, pfade: marke }] : []),
    ...(creator.length ? [{ art: "creator" as const, pfade: creator }] : []),
    ...rest.map((pfad) => ({ art: "sonst" as const, pfad })),
  ];
}

/** Laufzeit und Vertretung: kommen im Vertrag vor (contract.*), werden in Schritt 2 abgefragt (Befund 4) */
export const LAUFZEIT_PFADE = ["contract.starts_on", "contract.ends_on", "contract.term_months", "contract.notice_days", "contract.notice", "contract.auto_renew", "contract.renewal_months", "contract.tail_months", "contract.exclusive_management", "contract.exclusive_scope"] as const;
export function brauchtLaufzeit(html: string): boolean {
  return /\{\{#?(?:if\s+)?contract\.(starts_on|ends_on|term|notice|auto_renew|renewal|tail|exclusive)/.test(html);
}

/**
 * Schritt 1: passende Vorlagen (aktiv, Parteien passen zur Auswahl), die in der Sprache der Marke zuerst; vorgeschlagen wird
 * die erste Kooperations-Vorlage in dieser Sprache, sonst die erste passende.
 */
export function vorlagenFuer(templates: readonly TemplateRow[], opts: { sprache: Language; mitMarke: boolean; mitCreator: boolean }): { liste: TemplateRow[]; vorschlag: TemplateRow | null } {
  // Nachträge brauchen einen Ursprungsvertrag (Befund 9): sie entstehen über „Nachtrag“ am Vertrag, nicht im Assistenten
  const aktiv = templates.filter((x) => x.status === "active" && x.key !== "usage_extension");
  const passt = aktiv.filter((x) => (!x.required_signers.includes("brand") || opts.mitMarke) && (!x.required_signers.includes("talent") || opts.mitCreator));
  const liste = [...passt].sort((a, b) => Number(b.language === opts.sprache) - Number(a.language === opts.sprache) || a.name.localeCompare(b.name));
  const vorschlag = liste.find((x) => x.language === opts.sprache && x.key === "cooperation") ?? liste.find((x) => x.language === opts.sprache) ?? liste[0] ?? null;
  return { liste, vorschlag };
}

/** Titel aus Vorlage und Kooperation bzw. Creator (ohne „(Muster)“). */
export function titelVorschlag(vorlage: Pick<TemplateRow, "name"> | null, dealTitel: string | null, creator: string | null): string {
  const base = (vorlage?.name ?? t("Vertrag", "Contract")).replace(/\s*\((Muster|sample|Sample)\)\s*$/, "");
  return dealTitel ? `${base} ${dealTitel}` : creator ? `${base} ${creator}` : base;
}

export const SCHRITTE = [1, 2, 3, 4, 5] as const;
export type Schritt = (typeof SCHRITTE)[number];
export function schrittTitel(s: Schritt): string {
  switch (s) {
    case 1: return t("Worum geht es?", "What is it about?");
    case 2: return t("Was fehlt noch?", "What is still missing?");
    case 3: return t("Welche Klauseln?", "Which clauses?");
    case 4: return t("Wer unterschreibt?", "Who signs?");
    case 5: return t("Vorschau und Senden", "Preview and send");
  }
}
